import * as React from 'react';

/**
 * state of the Option for Derivate
 *
 * @export
 * @enum {number}
 */
export enum EOptionState {
    Hidden = -1, // Hidden is set internally!!!
    NotAvailable = 0, // empty => not available
    Optional, // empty circle => available but costs something
    Standard, // filled circle => standard equipment
    Available, // empty square => available without extra costs
    OptionalDraft, // grey empty check circle => available but costs something draft
    StandardDraft, // grey filled check circle => standard equipment draft
    AvailableDraft, // gray check square outline => available without extra costs draft
    StandardV, // filled circle with V superscript => PDiP assignment type
    StandardS, // filled circle with S superscript => PDiP assignment type
    StandardG, // filled circle with G superscript => PDiP assignment type
    StandardC, // filled circle with C superscript => PDiP assignment type
    StandardB, // filled circle with B superscript => PDiP assignment type
    OptionalF // empty circle with F superscript => PDiP assignment type
}
export const OptionStateCodeMap: any = {
    [EOptionState.NotAvailable]: "N",      // NICHT_ZUGEWIESEN
    [EOptionState.Optional]: "O",          // OPTIONAL
    [EOptionState.Standard]: "S",          // STANDARD
    [EOptionState.Available]: "W",         // WAHLWEISE
    [EOptionState.OptionalDraft]: "OE",    // OPTIONAL_ENTWURF
    [EOptionState.StandardDraft]: "SE",    // STANDARD_ENTWURF
    [EOptionState.AvailableDraft]: "WE",   // WAHLWEISE_ENTWURF
    [EOptionState.OptionalF]: "OF",        // OPTIONAL_F
    [EOptionState.StandardV]: "SV",        // STANDARD_V
    [EOptionState.StandardS]: "SS",        // STANDARD_S
    [EOptionState.StandardG]: "SG",        // STANDARD_G
    [EOptionState.StandardC]: "SC",        // STANDARD_C
    [EOptionState.StandardB]: "SB",        // STANDARD_B
    [EOptionState.Hidden]: "N",            // Hidden maps to NotAssigned/NICHT_ZUGEWIESEN
};
export const getOptionStateCodes: (states: Set<EOptionState>) => string[] = (states: Set<EOptionState>): string[] => {
    return Array.from(states)
        .map(state => OptionStateCodeMap[state])
        .filter(Boolean); // Remove undefined if any
};
/** assignment of derivates to its option states */
export type TDerivateStates = {
    [derivate: string]: EOptionState
};
/** onChange handler for request of changing of states
 *  Whether the state will be changed is the decision of the handler function,
 *  the state will not be changed internally in the DerivateOptionBar!
 * @param {string} derivate derivate, which option icon was clicked on
 * @param {EOptionState} state new option state for this derivate
 */
type OnChangeDerivatStateHandler = (derivate: string, state: EOptionState) => void;
/** Properties of DerivateOptionBar */
export interface IDerivateStateBarProps {
    /** list of all possible derivates */
    derivates: string[];
    /** list of derivates and their state, default state of derivate is "not available" */
    derivateStates: TDerivateStates;

    /** width of one state icon, the value is relation to padding
     *  the real width of whole bar can be set by width
     * @default 8
     */
    iconWidth?: number;
    /** space around an each state icon, the value is relation to iconWidth
     * @default 2
     */
    padding?: number;

    /** activates edit mode - in this mode the onChange events will be generated */
    editMode?: boolean;
    /**
     * if editMode is true, this event will be generated if the user clicks on some option icon.
     * Whether the state will be changed is the decision of the handler function,
     * the state will not be changed internally in the DerivateOptionBar!
     */
    onChange?: OnChangeDerivatStateHandler;
}

/** simple renderer props just for the form of option state icon */
interface ISimple {
    /** left upper corner of the icon: x-axis */
    x: number;
    /** left upper corner of the icon: y-axis */
    y: number;
    /** width of the icon */
    width: number;
    /** other properties */
    [key: string]: any;
}
/** simple renderer for rect */
function Rect(props: ISimple): JSX.Element {
    return <rect {...props} height={props.width} />;
}

/** simple renderer for circle */
function Circle(props: ISimple): JSX.Element {
    const r = props.width / 2;
    return <circle {...props} cx={props.x + r} cy={props.y + r} r={r} />;
}

/** properties of single option icon */
interface IOptionIconProps {
    derivate: string;
    form: EOptionState;
    x: number;
    y: number;
    width: number;
    tooltip?: string;
    editMode?: boolean;
    onChange?: OnChangeDerivatStateHandler;
}
/** state of single option icon */
interface IOptionIconState {
    renderer: (props: ISimple) => JSX.Element;
    strokeWidth: number;
    strokeDasharray: string;
    opacity: number;
}

/**
 * displays single option icon according to its type
 *
 * @class OptionIcon
 * @extends {React.Component<IOptionIconProps, IOptionIconState>}
 */
class OptionIcon extends React.Component<IOptionIconProps, IOptionIconState>
{
    private static getRenderer(form: EOptionState): any {
        return [EOptionState.Hidden, EOptionState.NotAvailable, EOptionState.Available].indexOf(form) !== -1 ? Rect : Circle;
    }

    private static getState(props: IOptionIconProps): IOptionIconState {
        const strokeWidth = Math.max(1, props.width / 8);
        let opacity = 0;
        switch (props.form) {
            case EOptionState.Hidden:
                // do nothing
                break;
            case EOptionState.NotAvailable:
                if (props.editMode) // in edit mode show a little bit of n.A.
                {
                    opacity = 0.1;
                }
                break;
            default:
                opacity = 1;
                break;
        }

        const state: IOptionIconState =
        {
            renderer: OptionIcon.getRenderer(props.form),
            strokeWidth: strokeWidth,
            strokeDasharray: EOptionState.NotAvailable === props.form && props.editMode ? (strokeWidth + ", " + strokeWidth) : "none",
            opacity
        };
        return state;
    }

    constructor(props: IOptionIconProps) {
        super(props);
        this.state = OptionIcon.getState(props);

        // This binding is necessary to make `this` work in the callback
        this.onChangeRequest = this.onChangeRequest.bind(this);
    }

    componentWillReceiveProps(newProps: IOptionIconProps) {
        // update only changed properties:
        let newState: IOptionIconState = {...this.state}; // not deep clone object
        if (newProps.form !== this.props.form
            || newProps.editMode !== this.props.editMode
            || newProps.width !== this.props.width
        ) {
            newState = OptionIcon.getState(newProps);
        }
        this.setState(newState);
    }

    onChangeRequest(): void {
        if (this.props.editMode && this.props.onChange && this.props.form !== EOptionState.Hidden) {
            const len = Object.keys(EOptionState).filter(k => typeof EOptionState[k as any] === "number").length - 1; // remove Hidden
            const newState: EOptionState = (this.props.form + 1) % len;
            this.props.onChange(this.props.derivate, newState);
        }
    }

    render() {
        const Figure = this.state.renderer;
        let className = "derivate-option-icon";
        if (this.props.form === EOptionState.NotAvailable ||
            this.props.form === EOptionState.Standard) {
            className += "-full";
        }
        return (
            <Figure
                className={className}
                x={this.props.x}
                y={this.props.y}
                width={this.props.width}
                strokeDasharray={this.state.strokeDasharray}
                opacity={this.state.opacity}
                strokeWidth={this.state.strokeWidth}
                onClick={this.onChangeRequest}
            >
                <title>{this.props.tooltip}</title>
            </Figure>
        );
    }
}

/** state of DerivateOptionBar */
interface IDerivateOptionBarState {
    /** width of one state icon
     * @default 5
     */
    iconWidth: number;

    /** space around an each state icon
     * @default 1
     */
    padding: number;
}

/**
 * displays the bar of option states for derivates
 *
 * @class DerivateOptionBar
 * @extends {React.Component<IDerivateStateBarProps, IDerivateOptionBarState>}
 */
class DerivateOptionBar extends React.Component<IDerivateStateBarProps, IDerivateOptionBarState>
{
    constructor(props: IDerivateStateBarProps) {
        super(props);
        this.state =
        {
            iconWidth: props.iconWidth ? props.iconWidth : 8,
            padding: props.padding ? props.padding : 2
        };
    }
    render() {
        const dist = this.state.iconWidth + this.state.padding * 2;
        let offs = this.state.padding - dist;
        const baseProps: IOptionIconProps =
        {
            derivate: "",
            form: EOptionState.NotAvailable,
            x: offs,
            y: this.state.padding,
            width: this.state.iconWidth,
            tooltip: "",
            editMode: this.props.editMode,
            onChange: this.props.onChange
        };
        const vbWidth = this.props.derivates.length * dist;
        const vbHeight = dist;

        return (
            <svg style={{height: "auto"}} viewBox={'0 0 ' + vbWidth + ' ' + vbHeight}>
                {
                    Array.from(this.props.derivates.values()).map((value: string, index: number) => {
                        let derivateState: EOptionState = this.props.derivateStates[value];
                        if (derivateState === undefined)
                            derivateState = EOptionState.NotAvailable;
                        return <OptionIcon key={value} {...baseProps} derivate={value} x={offs += dist} tooltip={value} form={derivateState} />;
                    }
                    )
                }
            </svg>
        );
    }
}
export default DerivateOptionBar;



// WEBPACK FOOTER //
// ./src/components/DerivateOptionBar.tsx